import type { ReactNode, ElementType, ComponentPropsWithoutRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../../utils/classMerge'
import { translate } from '../../../utils/translation'
import { getFontClass } from '../utils/get-font-class'

const weightVariants = cva('', {
  variants: {
    weightVariant: {
      mediumWeight: '[&_b]:tw-font-medium [&_strong]:tw-font-medium',
      boldWeight: '[&_b]:tw-font-bold [&_strong]:tw-font-bold',
      ultraWeight: '[&_b]:tw-font-black [&_strong]:tw-font-black',
    },
  },
  defaultVariants: {
    weightVariant: 'mediumWeight',
  },
})

interface BaseTextProps<T extends ElementType>
  extends VariantProps<typeof weightVariants> {
  as?: T
  children: ReactNode
  boldClassName?: string
  className?: string
  translationVariables?: Array<string | number | ReactNode>
  renderChildren?: boolean
  asChild?: boolean
}

type ConditionalTranslationProps<T extends ElementType> =
  | ({ skipTranslation: true; translationVariables?: never } & BaseTextProps<T>)
  | ({
      skipTranslation?: false
      translationVariables?: Array<string | number | ReactNode>
    } & BaseTextProps<T>)

const Text = <T extends ElementType = 'p'>({
  as,
  children,
  boldClassName,
  className,
  translationVariables = [],
  renderChildren = false,
  asChild = false,
  weightVariant,
  skipTranslation = false,
  ...props
}: ConditionalTranslationProps<T> & ComponentPropsWithoutRef<T>) => {
  const HTMLTag = as || 'p'
  const Component = asChild ? Slot : HTMLTag
  const fontClass = getFontClass(className, ['a', 'b', 'span'])
  const textClasses = cn(
    fontClass,
    weightVariants({ weightVariant }),
    className,
  )

  const renderText = () => {
    let html = skipTranslation
      ? children
      : translate(children, ...translationVariables)
    if (boldClassName && typeof html === 'string') {
      html = html.replace(
        /<b>(.+?)<\/b>/g,
        `<span class="${boldClassName}">$1</span>`,
      )
    }
    return html
  }

  if (renderChildren || asChild) {
    return (
      <Component className={textClasses} {...props}>
        {renderText()}
      </Component>
    )
  }

  return (
    <Component
      className={textClasses}
      dangerouslySetInnerHTML={{
        __html: renderText(),
      }}
      {...props}
    />
  )
}

export default Text
